/**
 * useUserTimezone — resolves the billing user's profile timezone from the API.
 *
 * Copied from packages/web-dispatch/src/lib/use-user-timezone.ts (Phase 12 /
 * Task 12.3). Calls GET /v1/users/me/profile and extracts `profile.timezone`
 * (and `profile.role` for the UX-only VIEW_INVOICES nav gate).
 *
 * DEC-004 (NON-NEGOTIABLE) — resolves the user's real profile timezone so all
 * invoice/period/generated-date labels are correct for non-Central users. Falls
 * back to America/Chicago (DEFAULT_TZ) only until the query resolves.
 *
 * Spec: §44 (local-by-context timestamps); DEC-004.
 */

import { useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { UserProfile } from '@lodex-pro/shared';
import { DEFAULT_TZ } from './timestamp.js';

/** TanStack Query cache key for the user profile endpoint. */
export const USER_PROFILE_QUERY_KEY = ['users', 'me', 'profile'] as const;

/** Response envelope from GET /v1/users/me/profile. */
interface UserProfileResponse {
  readonly profile: UserProfile;
}

/**
 * Returns the full user profile query result. Useful when the consumer also
 * needs the `role` (nav gate) in addition to `timezone`.
 *
 * staleTime: Infinity — a user's timezone has no mutation surface in the product,
 * so it cannot change during a session.
 *
 * The ROLE can, and this cache deliberately does not track it: PATCH
 * /v1/users/:id/role (api/src/routes/users/manage.ts) lets a MANAGE_USERS holder
 * change someone's role, and the server busts its own auth cache so the change
 * takes effect immediately. Because a caller may not change their OWN role, the
 * change always arrives from a different session, which this query cannot observe
 * without a refetch. The consequence is bounded and fails CLOSED: 'role' here
 * feeds only the UX nav gate, so a just-demoted user keeps seeing a nav entry
 * whose endpoints now 403, until they reload. Do not "fix" that by polling the
 * profile — the API is the trust boundary (three-layer security, CLAUDE.md §6).
 */
export function useUserProfile(): ReturnType<typeof useApiQuery<UserProfileResponse>> {
  const opts = useApiOpts();
  return useApiQuery<UserProfileResponse>(opts, '/v1/users/me/profile', {
    queryKey: USER_PROFILE_QUERY_KEY,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/**
 * Returns the billing user's profile timezone string (IANA format).
 *
 * Falls back to DEFAULT_TZ ('America/Chicago') until the profile query resolves.
 * This is the PRIMARY surface for DEC-004 compliance — pass the returned value as
 * the `timeZone` param to all timestamp formatters.
 */
export function useUserTimezone(): string {
  const { data } = useUserProfile();
  return data?.profile.timezone ?? DEFAULT_TZ;
}
